import { useState } from 'react';
import { FORM_FIELDS_CONFIG } from '../../constants';
import { useForm } from '../../hooks/useForm';
import { useLanguage } from '../../hooks/useLanguage';
import { classNames } from '../../utils';
import { contactService } from '../../services/api';
import { trackEvent } from '../../utils/analytics';
import '../../styles/components/Contact.css';

const Contact = () => {
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error'
  const [submitError, setSubmitError] = useState('');
  const { t } = useLanguage();

  const { formData, errors, isSubmitting, handleChange, handleSubmit } =
    useForm(
      { firstName: '', lastName: '', email: '', message: '' },
      t.contact.validation
    );

  const onSubmit = async data => {
    setSubmitStatus(null);
    setSubmitError('');

    let handled = false;
    try {
      const response = await contactService.send({
        name: `${data.firstName} ${data.lastName}`.trim(),
        email: data.email,
        message: data.message,
      });

      if (response.ok) {
        setSubmitStatus('success');
        trackEvent('contact_form_submit', { status: 'success' });
        return;
      }

      let errorMsg = t.contact.feedback.errorGeneric;
      if (response.status === 400) {
        const body = await response.json();
        errorMsg = Object.values(body.errors || {})[0]?.[0] || t.contact.feedback.errorInvalid;
      }

      handled = true;
      setSubmitStatus('error');
      setSubmitError(errorMsg);
      trackEvent('contact_form_submit', {
        status: 'error',
        error_type: response.status === 400 ? 'validation' : 'server',
      });
      throw new Error(errorMsg);
    } catch (err) {
      if (!handled) {
        setSubmitStatus('error');
        setSubmitError(t.contact.feedback.errorConnection);
        trackEvent('contact_form_submit', {
          status: 'error',
          error_type: 'connection',
        });
      }
      throw err;
    }
  };

  const renderFormField = field => {
    const label = t.contact.labels[field.id];

    return (
      <div key={field.id} className='form-group'>
        {field.type === 'textarea' ? (
          <textarea
            id={field.id}
            name={field.id}
            value={formData[field.id] || ''}
            onChange={handleChange}
            className={classNames('form-textarea', errors[field.id] && 'error')}
            placeholder={label}
            rows='5'
            required={field.required}
          />
        ) : (
          <input
            type={field.type}
            id={field.id}
            name={field.id}
            value={formData[field.id] || ''}
            onChange={handleChange}
            className={classNames('form-input', errors[field.id] && 'error')}
            placeholder={label}
            required={field.required}
          />
        )}
        {errors[field.id] && (
          <span className='error-message'>{errors[field.id]}</span>
        )}
      </div>
    );
  };

  const renderFormRow = () => {
    const rowFields = FORM_FIELDS_CONFIG.filter(
      field => field.id === 'firstName' || field.id === 'lastName'
    );

    return <div className='form-row'>{rowFields.map(renderFormField)}</div>;
  };

  const renderFormFields = () => {
    const remainingFields = FORM_FIELDS_CONFIG.filter(
      field => field.id !== 'firstName' && field.id !== 'lastName'
    );

    return remainingFields.map(renderFormField);
  };

  return (
    <section id='contact' className='contact section'>
      <div className='container'>
        <div className='contact-content'>
          <div className='contact-form-container'>
            <h2 className='contact-title'>{t.contact.title}</h2>
            <p className='contact-description'>{t.contact.description}</p>

            <form
              className='contact-form'
              onSubmit={e => {
                e.preventDefault();
                handleSubmit(onSubmit);
              }}
            >
              {renderFormRow()}
              {renderFormFields()}

              <button
                type='submit'
                className='submit-btn'
                disabled={isSubmitting || submitStatus === 'success'}
              >
                {isSubmitting ? t.contact.submitting : t.contact.submitButton}
              </button>

              {submitStatus === 'success' && (
                <p className='form-feedback form-feedback--success'>
                  {t.contact.feedback.success}
                </p>
              )}
              {submitStatus === 'error' && (
                <p className='form-feedback form-feedback--error'>
                  {submitError}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
